import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { TimepickerInterval } from './timepicker.types';

@Component({
  standalone: true,
  selector: 'fl-timepicker',
  imports: [MatTimepickerModule, ReactiveFormsModule],
  template: `
    <div
      class="Container"
      [attr.data-disabled]="isDisabled || undefined"
    >
      <input
        class="NativeElement"
        [attr.aria-label]="label"
        [attr.disabled]="isDisabled || undefined"
        [formControl]="control"
        [matTimepicker]="picker"
        [matTimepickerMin]="min"
        [matTimepickerMax]="max"
        [placeholder]="placeholder"
      />
      <mat-timepicker-toggle
        class="Toggle"
        [disabled]="isDisabled"
        [for]="picker"
      />
      <mat-timepicker
        #picker
        [interval]="interval"
      />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./timepicker.component.scss'],
})
export class TimepickerComponent {
  /** Expands to fill the parent width instead of fitting the time value. */
  @Input() @HostBinding('class.MaxWidth') maxWidth = false;

  /** FormControl that holds the selected time as a Date object. */
  @Input({ required: true }) control: FormControl<Date | undefined>;

  /** Time step between each option in the dropdown. */
  @Input() interval: TimepickerInterval = TimepickerInterval.THIRTY_MINUTES;

  /** Earliest selectable time. Only the time portion of the Date is used. */
  @Input() min: Date | undefined;

  /** Latest selectable time. Only the time portion of the Date is used. */
  @Input() max: Date | undefined;

  /** Placeholder text shown when no time is selected. */
  @Input() placeholder = '—— : ——  ——';

  /** Accessible label for the time input, used by screen readers. */
  @Input() label?: string;

  isDisabled = false;

  /** Disable the time input and toggle. */
  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
    if (value) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }
}
